import { useEffect, useState } from "react";
import { Sun, Moon, RefreshCw, FileDown, ChevronDown, FileJson, ImageDown, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export const TopStatusBar = ({
  loading = false,
  lastFetchedAt,
  marketRegime,
  dailyStatus,
  hasData = false,
  error,
  onRefresh,
  onExportJson,
  onExportPng,
}) => {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = now.toLocaleTimeString("en-GB", { hour12: false });
  const date = now.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const syncedLabel = lastFetchedAt
    ? lastFetchedAt.toLocaleTimeString("en-GB", { hour12: false })
    : "—";

  return (
    <div data-testid="top-status-bar" className="w-full border-b border-border bg-surface">
      <div className="flex items-center justify-between px-3 sm:px-5 py-2.5 gap-3 sm:gap-6">
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <svg width="22" height="22" viewBox="0 0 24 24" className="text-foreground">
              <path
                d="M2 12 L7 12 L9 6 L13 18 L15 9 L17 12 L22 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="square"
                strokeLinejoin="miter"
              />
            </svg>
            <span className="font-display font-black tracking-tight text-base text-foreground">
              PUSHNOW<span className="text-magenta"> DASHBOARD</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-1.5 pl-3 ml-1 border-l border-border">
            <Pill color={hasData ? "positive" : "neutral"} testId="status-data-source">
              <span
                className={`w-1.5 h-1.5 rounded-full pulse-dot ${
                  hasData ? "bg-positive" : "bg-text-secondary"
                }`}
              />
              {loading ? "SYNCING" : hasData ? "LIVE DATA" : "NO ROWS"}
            </Pill>
            <Pill color="neutral" testId="status-regime">
              {marketRegime || "REGIME —"}
            </Pill>
            <Pill color="neutral" testId="status-daily">
              {dailyStatus || "STATUS —"}
            </Pill>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono text-text-secondary">
          Last sync {syncedLabel}
          {error ? " · ERROR" : ""}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {user?.email && (
            <span
              className="hidden md:inline text-[10px] font-mono text-text-secondary max-w-[160px] truncate"
              data-testid="user-email"
              title={user.email}
            >
              {user.email}
            </span>
          )}
          <div className="hidden sm:flex flex-col items-end leading-tight">
            <span className="text-[10px] uppercase tracking-[0.18em] text-text-secondary">{date}</span>
            <span data-testid="status-time" className="text-xs font-mono num text-foreground">
              {time}
            </span>
          </div>
          <button
            data-testid="refresh-data-btn-header"
            onClick={onRefresh}
            disabled={loading}
            className="btn-ghost"
            aria-label="Refresh data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggle}
            className="btn-ghost"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="btn-ghost"
            aria-label="Logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="export-report-btn" className="btn-primary">
                <FileDown className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Export</span>
                <ChevronDown className="w-3 h-3 opacity-80" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={6}
              className="min-w-[200px] bg-surface border border-border text-foreground font-mono"
              style={{ borderRadius: 2 }}
            >
              <DropdownMenuItem
                data-testid="export-json-btn"
                onClick={onExportJson}
                className="text-[11px] uppercase tracking-[0.15em] cursor-pointer focus:bg-surface-muted"
              >
                <FileJson className="w-3.5 h-3.5 mr-2 text-positive" />
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="export-png-btn"
                onClick={onExportPng}
                className="text-[11px] uppercase tracking-[0.15em] cursor-pointer focus:bg-surface-muted"
              >
                <ImageDown className="w-3.5 h-3.5 mr-2 text-magenta" />
                Export as PNG
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const Pill = ({ children, color = "neutral", testId }) => {
  const map = {
    positive: "border-positive text-positive",
    magenta: "border-magenta text-magenta",
    neutral: "border-border text-text-secondary",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-mono border ${map[color]}`}
      style={{ borderRadius: 2 }}
    >
      {children}
    </span>
  );
};

export default TopStatusBar;
